import {
  AlertDialog,
  Banner,
  Button,
  CircleCheckBigIcon,
  Flex,
  Heading,
  Loader,
  Paragraph,
  Progress,
  Text,
} from '@buffer-mono/popcorn'

import type {
  ChannelMapping,
  ImportItem,
  ImportItemStatus,
} from '../MigrationWizard/types'
import { useImportExecution } from './useImportExecution'

import type { JSX } from 'react'

type ImportExecutionStepProps = {
  items: ImportItem[]
  channelMappings: ChannelMapping[]
  timezone: string
  onSaveProgress: (statuses: Record<string, ImportItemStatus>) => void
  onClose: () => void
  onAbort: () => void
  onContinueLater: () => void
  showDismissPrompt: boolean
  onDismissPromptClose: () => void
}

export function ImportExecutionStep({
  items,
  channelMappings,
  timezone,
  onSaveProgress,
  onClose,
  onAbort,
  onContinueLater,
  showDismissPrompt,
  onDismissPromptClose,
}: ImportExecutionStepProps): JSX.Element {
  const progress = useImportExecution({
    items,
    channelMappings,
    timezone,
    onSaveProgress,
  })

  const progressPercent =
    progress.total > 0
      ? Math.round((progress.processed / progress.total) * 100)
      : 0

  const handleDownloadErrors = (): void => {
    if (progress.errors.length === 0) return

    const csvContent = [
      'Row,Error',
      ...progress.errors.map(
        (e) => `${e.sourceRow},"${e.message.replace(/"/g, '""')}"`,
      ),
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'migration-import-errors.csv'
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <Flex direction="column" gap="lg">
      {progress.isRunning && (
        <>
          <Flex align="center" gap="sm">
            <Loader />
            <Heading size="small">
              Importing posts... {progressPercent}%
            </Heading>
          </Flex>

          <Flex direction="column" gap="xs">
            <Progress value={progressPercent} aria-label="Import progress" />
            <Text color="subtle" size="sm">
              {progress.processed} of {progress.total} posts processed
              {progress.failed > 0 && ` (${progress.failed} failed)`}
            </Text>
          </Flex>

          <Paragraph color="subtle" size="sm">
            Please keep this tab open while the import is running.
          </Paragraph>
        </>
      )}

      {progress.isComplete && (
        <>
          <Heading size="small" icon={<CircleCheckBigIcon color="success" />}>
            Import complete
          </Heading>

          <Flex direction="column" gap="xs">
            <Text>
              {progress.succeeded.toLocaleString()} post
              {progress.succeeded !== 1 ? 's' : ''} imported successfully.
            </Text>
            {progress.failed > 0 && (
              <Text color="critical">
                {progress.failed} post{progress.failed !== 1 ? 's' : ''} failed
                to import.
              </Text>
            )}
          </Flex>

          {progress.errors.length > 0 && (
            <Banner variant="critical">
              <Banner.Text>
                {progress.errors.length} error
                {progress.errors.length !== 1 ? 's' : ''} occurred during
                import.
              </Banner.Text>
              <Button
                variant="critical"
                size="small"
                onClick={handleDownloadErrors}
              >
                Download Error Log
              </Button>
            </Banner>
          )}

          <Flex gap="sm" justify="end">
            <Button variant="primary" onClick={onClose}>
              Done
            </Button>
          </Flex>
        </>
      )}
      <AlertDialog open={showDismissPrompt} onOpenChange={onDismissPromptClose}>
        <AlertDialog.Content>
          <AlertDialog.Title>Import in progress</AlertDialog.Title>
          <AlertDialog.Description>
            {progress.processed} of {progress.total} posts have been processed
            so far. What would you like to do?
          </AlertDialog.Description>
          <AlertDialog.Actions>
            <AlertDialog.Cancel>
              <Button variant="secondary" onClick={onDismissPromptClose}>
                Keep Importing
              </Button>
            </AlertDialog.Cancel>
            <AlertDialog.Action>
              <Button variant="primary" onClick={onContinueLater}>
                Continue Later
              </Button>
            </AlertDialog.Action>
            <AlertDialog.Action>
              <Button variant="critical" onClick={onAbort}>
                Abort Import
              </Button>
            </AlertDialog.Action>
          </AlertDialog.Actions>
        </AlertDialog.Content>
      </AlertDialog>
    </Flex>
  )
}
